import type { FieldOption } from '@/entities/profile';
import { Checkbox } from '@/shared/ui/checkbox';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { useTranslate } from '@tolgee/react';

export function RequiredMark({ required }: { required?: boolean }) {
  if (!required) return null;
  return <span className="text-destructive"> *</span>;
}

interface SelectFieldProps {
  id: string;
  label: string;
  options: FieldOption[];
  value?: number | string;
  onChange: (value: number) => void;
  required?: boolean;
  placeholder?: string;
}

/** Single-choice question stored as a legacy choice index. */
export function SelectField({
  id,
  label,
  options,
  value,
  onChange,
  required,
  placeholder,
}: SelectFieldProps) {
  const { t } = useTranslate();
  const displayValue =
    value === undefined || value === null || value === '' ? undefined : String(value);

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label}
        <RequiredMark required={required} />
      </Label>
      <Select value={displayValue} onValueChange={(v) => onChange(Number(v))}>
        <SelectTrigger id={id} data-testid={`select-${id}`} aria-required={required}>
          <SelectValue placeholder={placeholder ?? t('signup.placeholders.select')} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface MultiCheckFieldProps {
  id: string;
  label: string;
  options: FieldOption[];
  values: string[];
  onChange: (values: string[]) => void;
  required?: boolean;
  hint?: string;
  columns?: 2 | 3 | 4;
}

/** Multi-choice question: one legacy row per selected index. */
export function MultiCheckField({
  id,
  label,
  options,
  values,
  onChange,
  required,
  hint,
  columns = 3,
}: MultiCheckFieldProps) {
  const toggle = (value: string) => {
    onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value]);
  };
  const cols = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4' }[columns];

  return (
    <div className="space-y-2">
      <Label>
        {label}
        <RequiredMark required={required} />
      </Label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      <div className={`grid grid-cols-2 ${cols} gap-2`} data-testid={`multi-${id}`}>
        {options.map((opt) => (
          <div key={opt.value} className="flex items-center space-x-2 p-2 rounded-lg bg-muted/30">
            <Checkbox
              id={`${id}-${opt.value}`}
              checked={values.includes(opt.value)}
              onCheckedChange={() => toggle(opt.value)}
            />
            <label htmlFor={`${id}-${opt.value}`} className="text-sm cursor-pointer">
              {opt.label}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}
